import React, { useEffect } from 'react';
import { HashRouter, MemoryRouter, useLocation, useNavigate } from 'react-router-dom';

import UniTheme from '@src/common/ui/UniTheme';
import type { CheckoutProps } from '@src/components/Checkout/CheckoutLegacy';
import { useAppSelector, useWidgetParam } from '@src/core/hooks';
import { SelectedMethodProvider, useSelectedMethodIdInUrl } from '@src/features/payments';
import { directLinkStateSelector } from '@src/stores/directLink';

import { useCheckoutCompletedEvent, useCheckoutStartedEvent } from './analytics';
import { CheckoutFlowProvider } from './CheckoutFlowContext';
import { CheckoutLayout } from './CheckoutLayout';
import { CheckoutSummarySidebar } from './CheckoutSummarySidebar';
import { useCheckoutFlow, useRoutedCheckoutFlow } from './hooks/useCheckoutFlow';
import { useCheckoutPageClassNameSwap } from './hooks/useCheckoutPageClassNameSwap';
import { modeToPath, pathToMode, resolveCheckoutRouting } from './routing';
import { getInitialCheckoutMode } from './utils';
import { ViewContainer } from './styles';

type CheckoutWidgetProps = CheckoutProps;

type CheckoutWidgetBodyProps = CheckoutWidgetProps & {
  initialMode: ReturnType<typeof getInitialCheckoutMode>;
};

const HashRoutingBootstrap = (props: { initialPath: string }) => {
  const { initialPath } = props;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!pathToMode(location.pathname)) {
      navigate(initialPath, { replace: true });
    }
  }, [initialPath, location.pathname, navigate]);

  return null;
};

const CheckoutWidgetRouted = (props: CheckoutWidgetBodyProps) => {
  const { initialMode, ...flowParams } = props;
  const { mode, goTo, bootstrapping } = useRoutedCheckoutFlow(flowParams);

  // Under a router the picked method belongs in the URL, so a reload lands back on it instead of
  // silently falling to the first option. Without one nobody provides this and it stays in state.
  const selectedMethod = useSelectedMethodIdInUrl();

  return (
    <CheckoutFlowProvider mode={mode} goTo={goTo} bootstrapping={bootstrapping}>
      <SelectedMethodProvider value={selectedMethod}>
        <CheckoutLayout
          mode={mode}
          sidebar={<CheckoutSummarySidebar />}
          useRouting
          initialMode={initialMode}
          bootstrapping={bootstrapping}
        />
      </SelectedMethodProvider>
    </CheckoutFlowProvider>
  );
};

const CheckoutWidgetModeBased = (props: CheckoutWidgetProps) => {
  const { mode, goTo, bootstrapping } = useCheckoutFlow(props);

  return (
    <CheckoutFlowProvider goTo={goTo} mode={mode} bootstrapping={bootstrapping}>
      <CheckoutLayout mode={mode} sidebar={<CheckoutSummarySidebar />} bootstrapping={bootstrapping} />
    </CheckoutFlowProvider>
  );
};

export const CheckoutWidget = (props: CheckoutWidgetProps) => {
  const { skipResumeCart } = props;

  useCheckoutStartedEvent();
  useCheckoutCompletedEvent();

  const routing = resolveCheckoutRouting(useWidgetParam('use-routing'));
  const skipResumeCartPage = useWidgetParam('skip-resume-cart-page');
  const { directLink } = useAppSelector(directLinkStateSelector);
  const initialMode = getInitialCheckoutMode({ skipResumeCart, skipResumeCartPage, directLink });

  useCheckoutPageClassNameSwap();

  if (routing === 'memory') {
    return (
      <ViewContainer>
        <UniTheme>
          <MemoryRouter initialEntries={[modeToPath(initialMode)]}>
            <CheckoutWidgetRouted {...props} initialMode={initialMode} />
          </MemoryRouter>
        </UniTheme>
      </ViewContainer>
    );
  }

  if (routing === 'hash') {
    return (
      <ViewContainer>
        <UniTheme>
          <HashRouter>
            <HashRoutingBootstrap initialPath={modeToPath(initialMode)} />
            <CheckoutWidgetRouted {...props} initialMode={initialMode} />
          </HashRouter>
        </UniTheme>
      </ViewContainer>
    );
  }

  return (
    <ViewContainer>
      <UniTheme>
        <CheckoutWidgetModeBased {...props} />
      </UniTheme>
    </ViewContainer>
  );
};

export default CheckoutWidget;
